import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import type { StudyHome } from './studyApi';
import { createNoteReview, type NoteReviewWrite, type StudyNote } from './studyNotesApi';

const schema = z.object({
  title: z.string().trim().min(1, 'Describe what you will review.').max(180),
  due_on: z.union([z.literal(''), z.string().date()]),
  term_id: z.string(),
  subject_key: z.string(),
});
type Form = z.infer<typeof schema>;
const inputClass = 'mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3 text-sm';
export function StudyNoteReview({
  note,
  home,
  owner,
  enabled,
  onPendingChange,
}: {
  note: StudyNote;
  home?: StudyHome;
  owner: string;
  enabled: boolean;
  onPendingChange: (pending: boolean) => void;
}) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<NoteReviewWrite | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: 'Review these class notes',
      due_on: '',
      term_id: home?.current_term?.id ?? '',
      subject_key: '',
    },
  });
  const terms =
    home?.academic_years?.flatMap((y) =>
      y.terms.map((t) => ({ ...t, label: `${y.school_year} · ${t.label}` })),
    ) ??
    home?.terms ??
    [];
  const termId = form.watch('term_id');
  const subjects = terms.find((t) => t.id === termId)?.subjects ?? [];
  async function submit(values: Form) {
    if (inFlight.current || (!enabled && !pending)) return;
    inFlight.current = true;
    const request = pending ?? {
      request_id: crypto.randomUUID(),
      expected_version: note.version,
      title: values.title,
      due_on: values.due_on || null,
      term_id: values.term_id || null,
      subject_key: values.subject_key || null,
    };
    setPending(request);
    onPendingChange(true);
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      await createNoteReview(note.id, request);
      setPending(null);
      onPendingChange(false);
      setOpen(false);
      setSaved(true);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['study-note', owner, note.id] }),
        client.invalidateQueries({ queryKey: ['study-tasks'] }),
        client.invalidateQueries({ queryKey: ['study-home'] }),
        client.invalidateQueries({ queryKey: ['study-journey'] }),
      ]);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The review could not be saved.');
      if (
        failure &&
        typeof failure === 'object' &&
        'status' in failure &&
        typeof failure.status === 'number' &&
        failure.status >= 400 &&
        failure.status < 500
      ) {
        setPending(null);
        onPendingChange(false);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  return (
    <section
      className="mt-6 rounded-3xl border border-hairline bg-surface p-5"
      aria-label="Review these notes"
    >
      <h2 className="text-lg font-extrabold text-ink">Use these notes again</h2>
      <p className="mt-2 text-sm text-ink-soft">
        Create a separate review task. Choose its date and Term. Your notes and other task deadlines
        stay as saved.
      </p>
      {!enabled && !pending && (
        <p className="mt-2 text-sm">Save your note changes before adding a review.</p>
      )}
      {!open && (
        <button
          type="button"
          className="btn-pill-secondary mt-4"
          disabled={!enabled}
          onClick={() => {
            setOpen(true);
            setSaved(false);
          }}
        >
          Add to review
        </button>
      )}
      {open && (
        <form className="mt-4 grid gap-3 sm:grid-cols-2" onSubmit={form.handleSubmit(submit)}>
          <fieldset disabled={busy || Boolean(pending)} className="contents">
            <label className="text-sm font-bold">
              Review task
              <input className={inputClass} maxLength={180} {...form.register('title')} />
            </label>
            <label className="text-sm font-bold">
              Review date
              <input className={inputClass} type="date" {...form.register('due_on')} />
            </label>
            <label className="text-sm font-bold">
              Review Term
              <select
                aria-label="Review Term"
                className={inputClass}
                {...form.register('term_id')}
                onChange={(e) => {
                  form.setValue('term_id', e.target.value);
                  form.setValue('subject_key', '');
                }}
              >
                <option value="">Unfiled review</option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-bold">
              Review Subject
              <select
                aria-label="Review Subject"
                className={inputClass}
                {...form.register('subject_key')}
              >
                <option value="">No subject yet</option>
                {subjects.map((s) => (
                  <option key={s.subject_key} value={s.subject_key}>
                    {s.display_name}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
          <p className="text-xs text-slate2 sm:col-span-2">
            Uses saved note version {pending?.expected_version ?? note.version}. No AI call or Stars
            charge. Deleting these notes removes their linked review tasks.
          </p>
          {(form.formState.errors.title || form.formState.errors.due_on) && (
            <p role="alert">
              {form.formState.errors.title?.message ?? form.formState.errors.due_on?.message}
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm sm:col-span-2">
              {error} {pending && 'The result is unconfirmed. Retry the same review.'}
            </p>
          )}
          <div className="flex flex-wrap gap-3 sm:col-span-2">
            <button className="btn-pill-primary" disabled={busy || (!enabled && !pending)}>
              {busy ? 'Saving review…' : pending ? 'Retry review' : 'Confirm review task'}
            </button>
            <button
              className="btn-pill-secondary"
              type="button"
              disabled={busy || Boolean(pending)}
              onClick={() => setOpen(false)}
            >
              Cancel review
            </button>
          </div>
        </form>
      )}
      {saved && (
        <p className="mt-4 text-sm font-bold" role="status">
          Review saved in your plan.{' '}
          <Link to="/learn/study" className="underline">
            Open my plan
          </Link>
        </p>
      )}
      {(note.reviews?.length ?? 0) > 0 && (
        <ul className="mt-4 space-y-2">
          {note.reviews!.map((review) => (
            <li key={review.task_id} className="rounded-xl bg-canvas-pure p-3 text-sm">
              <Link
                className="font-bold underline"
                to={`/learn/study?task=${encodeURIComponent(review.task_id)}`}
              >
                {review.title}
              </Link>
              <p>
                {review.due_on ?? 'No review date'} · {review.status}
                {review.term_id
                  ? ` · ${terms.find((t) => t.id === review.term_id)?.label ?? 'Saved Term'}`
                  : ''}
              </p>
              {review.notebook_version !== note.version && (
                <p className="text-xs text-slate2">
                  Linked at version {review.notebook_version}. These notes have since changed.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
